import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import {
  DevErrorJson,
  useDevServerStore,
} from "@/store/zustand/devServerStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { Storyboard } from "@/utils/types";
import {
  useActiveCanvas,
  useActiveProject,
  useAutoUpdateRef,
  useCanvasId,
} from "@/utils/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { isNavTreeEmpty } from "../utils/navTreeLookupUtils";
import { track } from "@/utils/events/tracker";
import { TEMPLATE_PROJECT_PATHS } from "@/utils/templateProjects";
import {
  useCheckDevServerHtmlErrors,
  useProjectLaunchStatus,
} from "@/utils/devServerUtils";
import { StoryboardErrorType } from "@/components/Canvas/ErrorOverlay/ErrorOverlay";
import { useCanvasOverlayStates } from "@/components/Canvas/CanvasLoadingOverlay";

const useStoryboardNonResponsive = (storyboard: Storyboard) => {
  // Check if this storyboard is non-responsive
  const nonResponsiveSelector = useMemo(
    () =>
      (state: {
        applicationStoryboards: Record<string, { isNonResponsive?: boolean }>;
        tempobookIframe: { isNonResponsive?: boolean } | null;
      }) => {
        // Application storyboards have their own non-responsive status
        const appStoryboard = state.applicationStoryboards[storyboard.id];
        if (appStoryboard) {
          return appStoryboard.isNonResponsive || false;
        }

        // For registered storyboards, check the tempobook iframe's non-responsive status
        const tempobookIframe = state.tempobookIframe;
        return tempobookIframe?.isNonResponsive || false;
      },
    [storyboard.id],
  );

  const isNonResponsive = useIframePoolStore(nonResponsiveSelector);

  return {
    isNonResponsive,
  };
};

export const useStoryboardState = (
  storyboard: Storyboard,
): {
  error: DevErrorJson | null;
  slowToLoad: boolean;
  longLoading: boolean;
} => {
  // Canvas store data for display - use shallow comparison to prevent re-renders
  const navTreeForThisStoryboard = useCanvasStore(
    (store) => store.storyboardToNavTree?.[storyboard?.id],
  );

  const errorsForThisStoryboard = useDevServerStore(
    (store) => store.storyboardIdToErrors?.[storyboard?.id],
  );

  const slowToLoadForThisStoryboard = useDevServerStore(
    (store) => store.storyboardIdToSlowToLoad?.[storyboard?.id],
  );
  const manualFailedToLoadForThisStoryboard = useDevServerStore(
    (store) => store.storyboardIdToManualFailedToLoad?.[storyboard?.id],
  );
  const resetTimeoutsTrigger = useDevServerStore(
    (store) => store.resetTimeoutsTrigger?.[storyboard?.id],
  );

  const isEmptyNavTree = isNavTreeEmpty(navTreeForThisStoryboard);

  const { isNonResponsive } = useStoryboardNonResponsive(storyboard);

  // New status logic: Green if has nav tree and html, red if error, otherwise grey
  const hasNavTree = navTreeForThisStoryboard && !isEmptyNavTree;

  const nonApplicationRegisteredStoryboard = useIframePoolStore(
    (state) => state.registeredStoryboards?.[storyboard?.id],
  );
  const applicationRegisteredStoryboard = useIframePoolStore(
    (state) => state.applicationStoryboards?.[storyboard?.id],
  );

  const canvasId = useCanvasId();

  const project = useActiveProject();
  const repoNotConnected = useMemo(() => {
    if (!project?.repoPath) {
      return;
    }

    return TEMPLATE_PROJECT_PATHS.includes(project.repoPath?.toLowerCase());
  }, [project]);

  const canvasIdToDevServerUrl = useSelector(
    (state: any) => state.iframe.canvasIdToDevServerUrl,
  );
  const devServerUrl = canvasIdToDevServerUrl[canvasId];

  const registeredStoryboard =
    storyboard.type === STORYBOARD_TYPES.APPLICATION
      ? applicationRegisteredStoryboard
      : nonApplicationRegisteredStoryboard;

  const projectLaunchStatus = useProjectLaunchStatus();

  const navTreeNeeded = useMemo(() => {
    // If no dev server url yet, then not loading
    if (!devServerUrl || projectLaunchStatus !== "succeeded") {
      return false;
    }

    if (
      storyboard.renderStatus === "generating" ||
      storyboard.renderStatus === "placeholder"
    ) {
      return false;
    }

    if (
      !storyboard.created &&
      storyboard.type !== STORYBOARD_TYPES.APPLICATION
    ) {
      return false;
    }

    // If there are errors, we don't expect a nav tree so we don't wait for one
    if (errorsForThisStoryboard?.length) {
      return false;
    }

    return true;
  }, [
    devServerUrl,
    storyboard.type,
    storyboard.created,
    projectLaunchStatus,
    storyboard.renderStatus,
    errorsForThisStoryboard,
  ]);

  const loadedNavTreeSinceCanvasOpen = useMemo(() => {
    return Boolean(registeredStoryboard?.lastRefreshTs);
  }, [registeredStoryboard?.lastRefreshTs]);

  const needsFreshNavTree = Boolean(registeredStoryboard?.needsFreshNavTreeTs);

  const checkDevServerHtmlErrors = useCheckDevServerHtmlErrors();
  const checkDevServerHtmlErrorsRef = useAutoUpdateRef(
    checkDevServerHtmlErrors,
  );

  const { anyModalVisible } = useCanvasOverlayStates();

  const storyboardId = storyboard?.id;
  const storyboardType = storyboard?.type;

  useEffect(() => {
    if (projectLaunchStatus !== "succeeded" || anyModalVisible) {
      return;
    }

    if (!storyboardId) {
      return;
    }

    if (!navTreeNeeded) {
      useDevServerStore
        .getState()
        .setManualFailedToLoadForStoryboard(storyboardId, false);
      useDevServerStore
        .getState()
        .setSlowToLoadForStoryboard(storyboardId, false);
      return;
    }

    // If still lodading nav tree after 5 seconds, force show yellow dot (doesn't matter posthog flag)
    if (!hasNavTree || needsFreshNavTree || !loadedNavTreeSinceCanvasOpen) {
      // If it hasn't loaded in the first 2 seconds, request a fresh nav tree,
      // If it still hasn't loaded in 5 seconds, mark it as slow to load,
      // After 10 seconds, refresh the storyboard
      const retryTimeout = setTimeout(() => {
        if (storyboardType !== STORYBOARD_TYPES.APPLICATION) {
          useIframePoolStore.getState().requestNavTreeForAllStoryboards();
        } else {
          useIframePoolStore
            .getState()
            .requestNavTreeForApplicationStoryboard(storyboardId);
        }
        checkDevServerHtmlErrorsRef.current();
      }, 2 * 1000);

      const timeout1 = setTimeout(() => {
        useDevServerStore
          .getState()
          .setSlowToLoadForStoryboard(storyboardId, true);
        checkDevServerHtmlErrorsRef.current();
      }, 5 * 1000);

      const timeout2 = setTimeout(() => {
        useIframePoolStore.getState().refreshStoryboard(storyboardId);
      }, 10 * 1000);

      const timeout3 = setTimeout(() => {
        console.error({
          msg: "Nav tree not loaded, please see AI chat for errors",
          storyboardId: storyboardId,
        });
        useDevServerStore
          .getState()
          .setManualFailedToLoadForStoryboard(storyboardId, true);
        checkDevServerHtmlErrorsRef.current();
      }, 20 * 1000);

      return () => {
        clearTimeout(retryTimeout);
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        clearTimeout(timeout3);
      };
    } else {
      useDevServerStore
        .getState()
        .setManualFailedToLoadForStoryboard(storyboardId, false);
      useDevServerStore
        .getState()
        .setSlowToLoadForStoryboard(storyboardId, false);
    }
  }, [
    storyboardId,
    storyboardType,
    hasNavTree,
    needsFreshNavTree,
    loadedNavTreeSinceCanvasOpen,
    projectLaunchStatus,
    navTreeNeeded,
    resetTimeoutsTrigger,
    checkDevServerHtmlErrorsRef,
    anyModalVisible,
  ]);

  /**
   * Always clear overlays when clicking refresh
   */
  useEffect(() => {
    if (!storyboardId) {
      return;
    }

    useDevServerStore
      .getState()
      .setManualFailedToLoadForStoryboard(storyboardId, false);
    useDevServerStore
      .getState()
      .setSlowToLoadForStoryboard(storyboardId, false);
  }, [storyboardId, resetTimeoutsTrigger]);

  const errorResult = useMemo<DevErrorJson | null>(() => {
    // Direct known error
    if (errorsForThisStoryboard?.length) {
      return {
        ...errorsForThisStoryboard[0],
        errorType:
          errorsForThisStoryboard[0].errorType ||
          StoryboardErrorType.RUNTIME_ERROR,
      };
    }

    // If the storyboard is non-responsive, return a non-responsive error
    if (isNonResponsive) {
      return {
        message:
          "Storyboard is non-responsive - possible infinite re-render or hang",
        filename: storyboard.fullFilePath || "unknown",
        lineno: 1,
        colno: 1,
        stack: `The storyboard has stopped responding to heartbeat messages. This typically indicates:\n\n• An infinite re-render loop\n• A hanging operation blocking the UI thread\n• An unhandled promise or callback blocking execution\n\nConsider checking for:\n• useEffect dependencies causing infinite loops\n• Heavy synchronous operations\n• Unoptimized state updates`,
        count: 1,
        errorType: "NON_RESPONSIVE" as any,
      };
    }

    return null;
  }, [errorsForThisStoryboard]);

  const longLoading = useMemo(() => {
    if (projectLaunchStatus !== "succeeded" || errorResult || anyModalVisible) {
      return false;
    }

    const result =
      (registeredStoryboard as any)?.failedToLoad ||
      manualFailedToLoadForThisStoryboard;
    return result;
  }, [
    projectLaunchStatus,
    errorResult,
    anyModalVisible,
    registeredStoryboard,
    manualFailedToLoadForThisStoryboard,
  ]);

  const sentPosthogSlowToLoadRef = useRef(false);
  const sentPosthogErrorRef = useRef(false);
  const sentPosthogLongLoadingRef = useRef(false);

  useEffect(() => {
    if (longLoading && !sentPosthogLongLoadingRef.current) {
      sentPosthogLongLoadingRef.current = true;
      track("STORYBOARD_STATUS_LOG", {
        storyboardId: storyboard.id,
        status: "long_loading",
        fromTemplate: Boolean(project?.templateProjectId),
        repoConnected: !repoNotConnected,
      });
    } else {
      sentPosthogLongLoadingRef.current = false;
    }
  }, [longLoading]);

  const slowToLoad = useMemo(() => {
    if (projectLaunchStatus !== "succeeded" || errorResult || anyModalVisible) {
      return false;
    }

    return slowToLoadForThisStoryboard;
  }, [
    slowToLoadForThisStoryboard,
    projectLaunchStatus,
    errorResult,
    anyModalVisible,
  ]);

  useEffect(() => {
    if (slowToLoad && !sentPosthogSlowToLoadRef.current) {
      sentPosthogSlowToLoadRef.current = true;
      track("STORYBOARD_STATUS_LOG", {
        storyboardId: storyboard.id,
        status: "slow_to_load",
        msg: "Nav tree not yet loaded, please wait a moment...",
        fromTemplate: Boolean(project?.templateProjectId),
        repoConnected: !repoNotConnected,
      });
    } else {
      sentPosthogSlowToLoadRef.current = false;
    }
  }, [slowToLoad]);

  useEffect(() => {
    if (errorResult && !sentPosthogErrorRef.current) {
      sentPosthogErrorRef.current = true;
      track("STORYBOARD_STATUS_LOG", {
        storyboardId: storyboard.id,
        status: "error",
        msg: errorResult?.message,
        fromTemplate: Boolean(project?.templateProjectId),
        repoConnected: !repoNotConnected,
      });
    } else {
      sentPosthogErrorRef.current = false;
    }
  }, [errorResult]);

  return {
    error: errorResult,
    slowToLoad,
    longLoading,
  };
};

export const useAllStoryboardsWithErrorOverlays = () => {
  const storyboardIdToErrors = useDevServerStore(
    (store) => store.storyboardIdToErrors,
  );
  const manualFailedToLoadForThisStoryboard = useDevServerStore(
    (store) => store.storyboardIdToManualFailedToLoad,
  );
  const registeredStoryboards = useIframePoolStore(
    (state) => state.registeredStoryboards,
  );
  const applicationStoryboards = useIframePoolStore(
    (state) => state.applicationStoryboards,
  );

  const activeCanvas = useActiveCanvas();

  return useMemo(() => {
    return new Set([
      ...Object.keys(storyboardIdToErrors).filter((sbId) =>
        Boolean(storyboardIdToErrors[sbId]?.length),
      ),
      ...Object.keys(manualFailedToLoadForThisStoryboard).filter(
        (sbId) => manualFailedToLoadForThisStoryboard[sbId],
      ),
      ...Object.keys(registeredStoryboards).filter(
        (sbId) => registeredStoryboards[sbId].failedToLoad,
      ),
      ...Object.keys(applicationStoryboards).filter(
        (sbId) => applicationStoryboards[sbId].failedToLoad,
      ),
      ...Object.keys(applicationStoryboards).filter(
        (sbId) => applicationStoryboards[sbId].isNonResponsive,
      ),
    ]);
  }, [
    storyboardIdToErrors,
    manualFailedToLoadForThisStoryboard,
    registeredStoryboards,
    applicationStoryboards,
  ]);
};
